import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, AreaChart, Area, Legend,
} from "recharts";
import { TrendingUp, Container as ContainerIcon, DollarSign, Coins, Anchor, ArrowUpRight, Truck, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import DailyReportCard from "@/components/DailyReportCard";

const PALETTE = ["#0a2952", "#ff5b2c", "#14b8a6", "#22c55e", "#64748b", "#7c3aed"];

function fmtNum(n) { if (n === undefined || n === null) return "—"; return Number(n).toLocaleString("en-US"); }
function fmtMoney(n, sym) { if (n === undefined || n === null) return "—"; return `${sym}\u00a0${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`; }

const TABS = [
  { key: "inspection", label: "Inspection", icon: ContainerIcon },
  { key: "trucking", label: "Trucking", icon: Truck },
];

export default function Dashboard() {
  const [tab, setTab] = useState("inspection");
  const [inspStats, setInspStats] = useState(null);
  const [truckStats, setTruckStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/containers/stats/summary"),
          api.get("/trucking/stats/summary"),
        ]);
        if (!cancel) {
          setInspStats(a.data);
          setTruckStats(b.data);
        }
      } catch (e) {
        if (!cancel) setErr("Failed to load stats");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <DashboardSkeleton />;
  if (err) return <div className="p-6 text-red-600" data-testid="dashboard-error">{err}</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">Operations dashboard</div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl text-[#0a2952] tracking-tight leading-[1.05]">
            {tab === "inspection" ? "Inspection — Direct Gate In." : "Trucking — Per-Trip Billing."}
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            {tab === "inspection"
              ? "Real-time view of empty containers gated into MPS & GPHA at the Port of Tema, including inspection & service charges and toll & vann fees."
              : "Trucking trips for HPL, Arkas, and other shipping lines, billed per container in USD."}
          </p>
        </div>
        <Link
          to={tab === "inspection" ? "/inspection/records" : "/trucking/records"}
          data-testid="dashboard-cta-records"
          className="inline-flex items-center gap-2 self-start bg-[#ff5b2c] hover:bg-[#e54f24] text-white px-5 py-3 rounded-md font-semibold transition-all hover:-translate-y-[1px] hover:shadow"
        >
          Open Records <ArrowUpRight size={16} />
        </Link>
      </div>

      {/* Module tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="dashboard-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`dashboard-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 -mb-[2px] transition-colors ${
                active ? "border-[#ff5b2c] text-[#0a2952]" : "border-transparent text-slate-500 hover:text-[#0a2952]"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "inspection" && (
        <>
          <DailyReportCard module="inspection" />
          <InspectionView stats={inspStats} />
        </>
      )}
      {tab === "trucking" && (
        <>
          <DailyReportCard module="trucking" />
          <TruckingView stats={truckStats} />
        </>
      )}
    </div>
  );
}

function InspectionView({ stats }) {
  const totalContainers = stats?.total_containers || 0;
  const totalUsd = stats?.total_inspection_usd || 0;
  const totalGhc = stats?.total_toll_ghc || 0;
  const byType = stats?.by_type || [];
  const byLocation = stats?.by_location || [];
  const byDate = stats?.by_date || [];

  return (
    <div className="space-y-8" data-testid="dashboard-inspection-view">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Kpi icon={<ContainerIcon className="text-[#0a2952]" size={18} />} label="Total Containers" value={fmtNum(totalContainers)} accent="#0a2952" testId="kpi-total-containers" />
        <Kpi icon={<DollarSign className="text-[#ff5b2c]" size={18} />} label="Inspection & Service" value={fmtMoney(totalUsd, "USD")} sub="USD 4 / container" accent="#ff5b2c" testId="kpi-total-usd" />
        <Kpi icon={<Coins className="text-[#14b8a6]" size={18} />} label="Toll & Vann" value={fmtMoney(totalGhc, "GHC")} sub="GHC 35 / container" accent="#14b8a6" testId="kpi-total-ghc" />
        <Kpi icon={<TrendingUp className="text-[#22c55e]" size={18} />} label="Avg / Day" value={fmtNum(byDate.length ? Math.round(totalContainers / byDate.length) : 0)} sub={`${byDate.length} active days`} accent="#22c55e" testId="kpi-avg-per-day" />
      </div>

      <Card title="Containers Per Day" subtitle="Daily gate-in volume" testId="chart-daily-trend">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={byDate.map((d) => ({ ...d, label: d.date.slice(5) }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradTrend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff5b2c" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#ff5b2c" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11, fontFamily: "IBM Plex Sans" }} stroke="#cbd5e1" />
            <YAxis tick={{ fill: "#64748b", fontSize: 11, fontFamily: "IBM Plex Sans" }} stroke="#cbd5e1" allowDecimals={false} />
            <Tooltip content={<DarkTooltip />} cursor={{ stroke: "#0a2952", strokeOpacity: 0.15 }} />
            <Area type="monotone" dataKey="count" name="Containers" stroke="#ff5b2c" strokeWidth={2.5} fill="url(#gradTrend)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2" title="By Type / Size" subtitle="Container mix on direct gate-in" testId="chart-by-type">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byType} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="type_size" tick={{ fill: "#0a2952", fontSize: 12, fontWeight: 600 }} stroke="#cbd5e1" />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} stroke="#cbd5e1" allowDecimals={false} />
              <Tooltip content={<DarkTooltip />} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {byType.map((d, i) => (<Cell key={d.type_size} fill={PALETTE[i % PALETTE.length]} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="By Location" subtitle="MPS vs GPHA gates" testId="chart-by-location">
          <BreakdownBars rows={byLocation} keyField="location" />
        </Card>
      </div>

      <Card title="Charges Over Time" subtitle="Daily inspection (USD) & toll-vann (GHC) accruals" testId="chart-charges-time">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={byDate.map((d) => ({ ...d, label: d.date.slice(5) }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} stroke="#cbd5e1" />
            <YAxis yAxisId="left" tick={{ fill: "#ff5b2c", fontSize: 11 }} stroke="#fcd1c1" allowDecimals={false} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: "#14b8a6", fontSize: 11 }} stroke="#a7f3d0" allowDecimals={false} />
            <Tooltip content={<DarkTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, fontWeight: 600, color: "#0a2952" }} />
            <Line yAxisId="left" type="monotone" dataKey="usd" name="Inspection USD" stroke="#ff5b2c" strokeWidth={2.5} dot={false} />
            <Line yAxisId="right" type="monotone" dataKey="ghc" name="Toll & Vann GHC" stroke="#14b8a6" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function TruckingView({ stats }) {
  const total = stats?.total_records || 0;
  const totalUsd = stats?.total_usd || 0;
  const byLine = stats?.by_shipping_line || [];
  const byType = stats?.by_type || [];
  const byDate = stats?.by_date || [];

  if (total === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="trucking-empty-state">
        <Truck size={48} className="mx-auto text-slate-300 mb-3" />
        <div className="font-display font-extrabold text-2xl text-[#0a2952] mb-2">No trucking trips yet</div>
        <p className="text-slate-600 mb-5 max-w-md mx-auto">Once you start logging trucking trips, you'll see daily volume, USD billed, and a breakdown by shipping line right here.</p>
        <Link to="/trucking/entry" className="inline-flex items-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] text-white px-5 py-3 rounded-md font-semibold">
          Start logging trips <ArrowUpRight size={16} />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-trucking-view">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Kpi icon={<Truck className="text-[#0a2952]" size={18} />} label="Total Trips" value={fmtNum(total)} accent="#0a2952" testId="trucking-kpi-total" />
        <Kpi icon={<DollarSign className="text-[#ff5b2c]" size={18} />} label="USD Billed" value={fmtMoney(totalUsd, "USD")} sub={`Avg USD ${total ? (totalUsd / total).toFixed(2) : "0"} / trip`} accent="#ff5b2c" testId="trucking-kpi-usd" />
        <Kpi icon={<Building2 className="text-[#14b8a6]" size={18} />} label="Shipping Lines" value={fmtNum(byLine.length)} sub={byLine.map(l => l.shipping_line).join(", ")} accent="#14b8a6" testId="trucking-kpi-lines" />
        <Kpi icon={<TrendingUp className="text-[#22c55e]" size={18} />} label="Avg / Day" value={fmtNum(byDate.length ? Math.round(total / byDate.length) : 0)} sub={`${byDate.length} active days`} accent="#22c55e" testId="trucking-kpi-avg" />
      </div>

      <Card title="Trips Per Day" subtitle="Daily trucking volume" testId="trucking-chart-daily">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={byDate.map((d) => ({ ...d, label: d.date.slice(5) }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradTrucking" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0a2952" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#0a2952" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} stroke="#cbd5e1" />
            <YAxis tick={{ fill: "#64748b", fontSize: 11 }} stroke="#cbd5e1" allowDecimals={false} />
            <Tooltip content={<DarkTooltip />} cursor={{ stroke: "#0a2952", strokeOpacity: 0.15 }} />
            <Area type="monotone" dataKey="count" name="Trips" stroke="#0a2952" strokeWidth={2.5} fill="url(#gradTrucking)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2" title="By Shipping Line" subtitle="Trips & USD billed per line" testId="trucking-chart-by-line">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byLine} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="shipping_line" tick={{ fill: "#0a2952", fontSize: 12, fontWeight: 600 }} stroke="#cbd5e1" />
              <YAxis yAxisId="left" tick={{ fill: "#0a2952", fontSize: 11 }} stroke="#cbd5e1" allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#ff5b2c", fontSize: 11 }} stroke="#fcd1c1" allowDecimals={false} />
              <Tooltip content={<DarkTooltip />} cursor={{ fill: "#f1f5f9" }} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 600 }} />
              <Bar yAxisId="left" dataKey="count" name="Trips" fill="#0a2952" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="usd" name="USD" fill="#ff5b2c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="By Container Size" subtitle="Trip mix" testId="trucking-chart-by-size">
          <BreakdownBars rows={byType} keyField="type_size" />
        </Card>
      </div>
    </div>
  );
}

function BreakdownBars({ rows, keyField }) {
  if (!rows?.length) return <div className="text-sm text-slate-500">No data.</div>;
  const total = rows.reduce((a, b) => a + b.count, 0) || 1;
  return (
    <div className="space-y-3">
      {rows.map((row, i) => {
        const value = row[keyField];
        const pct = (row.count / total) * 100;
        const color = PALETTE[i % PALETTE.length];
        return (
          <div key={value} data-testid={`bd-row-${value}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-semibold text-[#0a2952] flex items-center gap-2">
                <Anchor size={14} style={{ color }} /> {value}
              </span>
              <span className="font-mono-tabular text-slate-700">{fmtNum(row.count)}</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-sm overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono-tabular">{pct.toFixed(1)}%</div>
          </div>
        );
      })}
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="relative bg-white border border-slate-200 rounded-md p-6 hover:border-[#0a2952]/40 transition-colors overflow-hidden group">
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} />
      <div className="flex items-start justify-between mb-4">
        <div className="overline">{label}</div>
        <div className="w-9 h-9 rounded-md bg-slate-50 grid place-items-center group-hover:scale-110 transition-transform">{icon}</div>
      </div>
      <div className="font-display font-extrabold text-3xl lg:text-[2.25rem] text-[#0a2952] leading-none tracking-tight font-mono-tabular">{value}</div>
      {sub && <div className="mt-2 text-xs text-slate-500 truncate">{sub}</div>}
    </div>
  );
}

function Card({ title, subtitle, children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`bg-white border border-slate-200 rounded-md p-6 ${className}`}>
      <div className="mb-5">
        <div className="overline mb-1">{title}</div>
        {subtitle && <div className="text-sm text-slate-500">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function DarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a2952] text-white px-3 py-2 rounded-md text-xs shadow-lg border border-[#0a2952]">
      <div className="font-semibold mb-1 opacity-80">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center justify-between gap-4">
          <span className="opacity-80" style={{ color: p.color }}>{p.name}</span>
          <span className="font-mono-tabular font-semibold">{Number(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="h-12 w-2/3 bg-slate-200 rounded" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 bg-slate-100 rounded-md border border-slate-200" />)}
      </div>
      <div className="h-72 bg-slate-100 rounded-md border border-slate-200" />
    </div>
  );
}
